import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accrualTypes, visibleAccrualTypes, VALUE_KIND_LABELS,
  employees, plannedAccrualOrders, staffSchedules,
  type PlannedAccrualItem,
} from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

/**
 * Редактор «Наказ про зміну нарахувань» — сторінка-редактор за спільним патерном
 * кадрових наказів (HireOrderEditor): card+toolbar+кнопки, usePermissionsByOrg,
 * useFormValidation, useNumberPreview. Веде регістр планових нарахувань (рядки
 * ADD/CHANGE/CLOSE з дати дії) + кадровий стан для базового окладу. payroll.manage.
 */

const ACTION_LABELS: Record<string, string> = {
  ADD: 'Додати', CHANGE: 'Змінити', CLOSE: 'Закрити',
};

interface ItemForm extends PlannedAccrualItem {}

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_ITEM: ItemForm = {
  employee_id: '', accrual_type_id: '', action: 'ADD',
  value_kind: 'FIXED_SUM', value: '0',
};

export default function PlannedAccrualOrderEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const {
    availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs, loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['planned-accrual-order', id],
    queryFn: () => plannedAccrualOrders.get(id!),
    enabled: !isNew,
  });

  const [orgId, setOrgId] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentDate, setDocumentDate] = useState(today());
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [items, setItems] = useState<ItemForm[]>([{ ...EMPTY_ITEM }]);
  const [posted, setPosted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = useFormValidation();
  const numberPreview = useNumberPreview('doc.planned_accrual_orders', orgId, isNew);

  // Масове додавання.
  const [bulkEmpIds, setBulkEmpIds] = useState<string[]>([]);
  const [bulkTypeId, setBulkTypeId] = useState('');
  const [bulkAction, setBulkAction] = useState<PlannedAccrualItem['action']>('ADD');
  const [bulkValue, setBulkValue] = useState('0');

  // Заповнення зі штатного розкладу (масове призначення окладів по посаді+підрозділу).
  const [ssDocId, setSsDocId] = useState('');
  const [ssTypeId, setSsTypeId] = useState('');
  const ssListQ = useQuery({
    queryKey: ['staff-schedules', orgId],
    queryFn: () => staffSchedules.list(orgId),
    enabled: !!orgId,
  });

  // Лише ШТАТНІ + чинні (планові ведуться тільки для штату).
  const empQ = useQuery({
    queryKey: ['employees-for-pao', orgId],
    queryFn: () => employees.list({
      organization_id: orgId, employment_type: 'STAFF', active_only: true, limit: 1000,
    }),
    enabled: !!orgId,
  });
  const typesQ = useQuery({ queryKey: ['accrual-types'], queryFn: accrualTypes.list });
  // Видимі види: 4 системних + імпортовані (рішення власника, як на сторінці видів).
  const visibleTypes = visibleAccrualTypes(typesQ.data).filter((t) => t.is_active);

  useEffect(() => {
    if (isNew) {
      setOrgId((o) => o || (availableOrgs[0]?.id ?? ''));
    } else if (docQuery.data) {
      const d = docQuery.data;
      setOrgId(d.organization_id);
      setDocumentNumber(d.document_number);
      setDocumentDate(d.document_date.slice(0, 10));
      setEffectiveDate(d.effective_date);
      setItems(d.items.map((it) => ({ ...it })));
      setPosted(d.posted);
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && posted;

  // value_kind рядка — завжди з обраного виду (для відображення «Спосіб» read-only
  // та для збереження/розрахунку). Тип — джерело істини способу (рішення ч.134).
  const itemValueKind = (it: ItemForm): 'FIXED_SUM' | 'PERCENT' | 'RATE' =>
    typesQ.data?.find((t) => t.id === it.accrual_type_id)?.value_kind ?? it.value_kind ?? 'FIXED_SUM';

  const buildPayload = () => ({
    organization_id: orgId,
    document_date: `${documentDate}T00:00:00Z`,
    effective_date: effectiveDate,
    items: items.map((it) => ({
      employee_id: it.employee_id, accrual_type_id: it.accrual_type_id,
      // «Спосіб» = ЗАВЖДИ з виду нарахування (itemValueKind), не з застарілого
      // it.value_kind рядка. Інакше UI показує спосіб типу (напр. FIXED_SUM), а
      // у БД лишається старий PERCENT → розрахунок як % замість суми.
      action: it.action, value_kind: itemValueKind(it),
      value: it.action === 'CLOSE' ? null : it.value,
    })),
  });

  const create = useMutation({
    mutationFn: () => plannedAccrualOrders.create(buildPayload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['planned-accrual-orders'] });
      navigate(`/payroll/planned-accrual-orders/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => {
      const { organization_id: _o, ...rest } = buildPayload();
      return plannedAccrualOrders.update(id!, rest);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planned-accrual-order', id] });
      qc.invalidateQueries({ queryKey: ['planned-accrual-orders'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      const { organization_id: _o, ...rest } = buildPayload();
      await plannedAccrualOrders.update(id!, rest);
      await plannedAccrualOrders.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planned-accrual-order', id] });
      qc.invalidateQueries({ queryKey: ['planned-accrual-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => plannedAccrualOrders.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planned-accrual-order', id] });
      qc.invalidateQueries({ queryKey: ['planned-accrual-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => plannedAccrualOrders.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planned-accrual-orders'] });
      navigate('/hr-orders');
    },
  });

  function validate(): boolean {
    if (!v.check([
      ['organization_id', orgId, 'Оберіть юрособу'],
      ['effective_date', effectiveDate, 'Вкажіть дату дії'],
    ])) return false;
    if (!items.length || items.some((it) => !it.employee_id || !it.accrual_type_id)) {
      setError('Заповніть працівника і вид у кожному рядку.');
      return false;
    }
    setError(null);
    return true;
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  // Сторінка створення: зберегти чернетку і одразу провести (create → post → картка).
  async function onSaveAndPost() {
    if (!validate()) return;
    try {
      const doc = await create.mutateAsync();
      await plannedAccrualOrders.post(doc.id);
      qc.invalidateQueries({ queryKey: ['planned-accrual-orders'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      navigate(`/payroll/planned-accrual-orders/${doc.id}`);
    } catch (e) {
      setError(extractError(e));
    }
  }
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  function onUpdateAndPost() {
    if (!validate()) return;
    updateAndPost.mutate();
  }

  const setItem = (idx: number, patch: Partial<ItemForm>) =>
    setItems((arr) => arr.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      // «Спосіб» (value_kind) тягнеться з обраного виду нарахування — не вибирається
      // вручну. При зміні виду підставляємо його дефолтний value_kind.
      if ('accrual_type_id' in patch) {
        const at = typesQ.data?.find((t) => t.id === patch.accrual_type_id);
        if (at) next.value_kind = at.value_kind;
      }
      return next;
    }));

  // UX β: warning якщо новий, а прав немає у жодній org.
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e', maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати наказ про зміну нарахувань у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до адміністратора.
          </div>
          <button onClick={() => navigate('/hr-orders')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий наказ (зміна нарахувань)' : `Наказ (зміна нарахувань) № ${documentNumber}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/hr-orders')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postPending={create.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/payroll/planned-accrual-orders/${id}/print`)}
        />
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          {/* Зміна юрособи скидає рядки (інший список штатних). Лише новий — у наказа з id зафіксовано. */}
          <select value={orgId}
                  onChange={(e) => { setOrgId(e.target.value); setItems([{ ...EMPTY_ITEM }]); }}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Дата документа{isNew && numberPreview ? ` (№ буде: ${numberPreview})` : ''}</label>
          <DatePopover value={documentDate}
                 onChange={(val) => setDocumentDate(val)}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Діє з</label>
          <DatePopover value={effectiveDate}
                 onChange={(val) => setEffectiveDate(val)}
                 disabled={isPosted} />
        </div>
      </div>

      {!orgId && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          Оберіть юрособу, щоб додавати рядки нарахувань.
        </p>
      )}

      <h3 style={{ marginTop: '1.25rem' }}>Рядки нарахувань</h3>
      <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th>Працівник</th><th>Вид</th><th>Спосіб</th><th>Дія</th><th>Значення</th><th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx}>
              <td>
                <select value={it.employee_id} disabled={isPosted}
                        onChange={(e) => setItem(idx, { employee_id: e.target.value })}>
                  <option value="">—</option>
                  {empQ.data?.items.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.full_name ?? emp.code}</option>
                  ))}
                </select>
              </td>
              <td>
                <select value={it.accrual_type_id} disabled={isPosted}
                        onChange={(e) => setItem(idx, { accrual_type_id: e.target.value })}>
                  <option value="">—</option>
                  {visibleTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </td>
              <td>
                {/* «Спосіб» тягнеться з виду нарахування (read-only), не вибирається. */}
                <span style={{ color: it.accrual_type_id ? '#334155' : '#94a3b8', fontSize: '0.82rem' }}>
                  {it.accrual_type_id ? VALUE_KIND_LABELS[itemValueKind(it)] : '—'}
                </span>
              </td>
              <td>
                <select value={it.action} disabled={isPosted}
                        onChange={(e) => setItem(idx, { action: e.target.value as PlannedAccrualItem['action'] })}>
                  {Object.entries(ACTION_LABELS).map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                </select>
              </td>
              <td>
                <input type="number" value={it.value ?? ''} disabled={isPosted || it.action === 'CLOSE'}
                       style={{ width: 90 }}
                       onChange={(e) => setItem(idx, { value: e.target.value })} />
              </td>
              <td>
                {!isPosted && items.length > 1 && (
                  <button className="danger"
                          onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}>✕</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && (
        <button style={{ marginTop: '0.5rem' }}
                onClick={() => setItems((arr) => [...arr, { ...EMPTY_ITEM }])}>
          + Додати рядок
        </button>
      )}

      {/* Масове додавання: обрати кількох співробітників + спільні параметри. */}
      {!isPosted && (
        <div style={{ marginTop: '0.75rem', padding: '0.6rem', background: '#f8fafc',
                      border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <strong style={{ fontSize: '0.85rem' }}>Масове додавання</strong>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                        marginTop: '0.4rem', flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 220px', fontSize: '0.8rem' }}>Співробітники (Ctrl/Shift — кілька)
              <select multiple value={bulkEmpIds} size={5} style={{ width: '100%' }}
                      onChange={(e) => setBulkEmpIds(Array.from(e.target.selectedOptions, (o) => o.value))}>
                {empQ.data?.items.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.full_name ?? emp.code}</option>
                ))}
              </select>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.8rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                <label>Вид
                  <select value={bulkTypeId} onChange={(e) => setBulkTypeId(e.target.value)}>
                    <option value="">—</option>
                    {visibleTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <label>Спосіб
                  {/* Тягнеться з обраного виду (read-only), не вибирається. */}
                  <div style={{ padding: '0.35rem 0', color: bulkTypeId ? '#334155' : '#94a3b8' }}>
                    {bulkTypeId
                      ? VALUE_KIND_LABELS[typesQ.data?.find((t) => t.id === bulkTypeId)?.value_kind ?? 'FIXED_SUM']
                      : '—'}
                  </div>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                <label>Дія
                  <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value as PlannedAccrualItem['action'])}>
                    {Object.entries(ACTION_LABELS).map(([val, l]) => <option key={val} value={val}>{l}</option>)}
                  </select>
                </label>
                <label>Значення
                  <input type="number" value={bulkValue} disabled={bulkAction === 'CLOSE'} style={{ width: 90 }}
                         onChange={(e) => setBulkValue(e.target.value)} />
                </label>
                <button className="primary"
                        disabled={bulkEmpIds.length === 0 || !bulkTypeId}
                        onClick={() => {
                          // Спосіб (value_kind) — з обраного виду нарахування.
                          const bulkVk = typesQ.data?.find((t) => t.id === bulkTypeId)?.value_kind ?? 'FIXED_SUM';
                          const newItems = bulkEmpIds.map((eid) => ({
                            ...EMPTY_ITEM, employee_id: eid, accrual_type_id: bulkTypeId,
                            action: bulkAction, value_kind: bulkVk,
                            value: bulkAction === 'CLOSE' ? null : bulkValue,
                          }));
                          setItems((arr) => {
                            const base = arr.length === 1 && !arr[0]?.employee_id ? [] : arr;
                            return [...base, ...newItems];
                          });
                          setBulkEmpIds([]);
                        }}>
                  Додати обраним ({bulkEmpIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Заповнити зі штатного розкладу — масове призначення окладів по посаді+підрозділу. */}
      {!isPosted && (
        <div style={{ marginTop: '0.75rem', padding: '0.6rem', background: '#f0f9ff',
                      border: '1px solid #bae6fd', borderRadius: 6 }}>
          <strong style={{ fontSize: '0.85rem' }}>Заповнити зі штатного розкладу</strong>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end',
                        marginTop: '0.4rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
            <label>Документ штатного розкладу
              <select value={ssDocId} onChange={(e) => setSsDocId(e.target.value)}>
                <option value="">—</option>
                {(ssListQ.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.document_number} від {d.document_date.slice(0, 10)}
                    {d.posted ? '' : ' (чернетка)'}
                  </option>
                ))}
              </select>
            </label>
            <label>Вид нарахування (оклад)
              <select value={ssTypeId} onChange={(e) => setSsTypeId(e.target.value)}>
                <option value="">—</option>
                {visibleTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <button
              disabled={!ssDocId || !ssTypeId}
              onClick={async () => {
                try {
                  const emps = await staffSchedules.resolveEmployees(ssDocId);
                  if (emps.length === 0) {
                    setError('У штатному розкладі немає активних працівників за цими посадами/підрозділами.');
                    return;
                  }
                  const newItems = emps.map((e) => ({
                    ...EMPTY_ITEM, employee_id: e.employee_id, accrual_type_id: ssTypeId,
                    action: 'CHANGE' as const, value_kind: 'FIXED_SUM' as const,
                    value: e.salary,
                  }));
                  setItems((arr) => {
                    const base = arr.length === 1 && !arr[0]?.employee_id ? [] : arr;
                    return [...base, ...newItems];
                  });
                  setError(null);
                } catch {
                  setError('Не вдалося завантажити працівників зі штатного розкладу.');
                }
              }}
            >
              Заповнити окладами ({(ssListQ.data ?? []).length} док.)
            </button>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '0.3rem 0 0' }}>
            Підтягне активних працівників за посадами+підрозділами зі штатного розкладу
            та проставить їхні оклади (дія «Змінити», спосіб «Сума»). Перевірте перед
            проведенням.
          </p>
        </div>
      )}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          При проведенні: рядки застосуються у регістрі планових нарахувань з дати дії
          (старі однакові — авто-закриються). Зміна базового окладу оновить і «Поточний
          стан» працівника. Бухгалтерських проводок документ не робить.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string }; status?: number } }).response;
    if (r?.status === 403) return 'Немає права (потрібен дозвіл payroll.manage).';
    return r?.data?.detail ?? 'Помилка збереження';
  }
  return String(e);
}
